// store/slices/auth/authSlice.ts
import { createSlice } from '@reduxjs/toolkit';

import { extractFieldErrors } from '@/store/utils';
import { credentialVerification } from '@/store/thunks/auth/credentialVerificationThunk';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import { verifyOtpThunk } from '@/store/thunks/auth/verifyOtpThunk';
import { sendEmailCodeThunk } from '@/store/thunks/auth/sendEmailCodeThunk';
import { initialAuthState } from '@/types/auth/AuthState';
import { sendPhoneCodeThunk } from '@/store/thunks/auth/sendPhoneThunk';
import { forgotPasswordThunk } from '@/store/thunks/auth/forgotPasswordThunk';
import { verifyRecoveryOtpThunk } from '@/store/thunks/auth/verifyRecoveryOtpThunk';
import { sendRecoveryTwoFactorThunk } from '@/store/thunks/auth/sendRecoveryTwoFactorThunk';
import { verifyRecoveryTwoFactorThunk } from '@/store/thunks/auth/verifyRecoveryTwoFactorThunk';

// ✅ Simpler state - no step tracking
const initialState = initialAuthState;
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearLoginError(state) {
      state.error = undefined;
      state.fieldErrors = {};
    },
    setPassword(state, action: { payload: string }) {
      state.password = action.payload;
    },
    setUkek(state, action: { payload: string }) {
      state.ukek = action.payload;
    },
    setDecapsKey(state, action: { payload: string }) {
      state.decapsKey = action.payload;
    },
    setUserId(state, action: { payload: string | null }) {
      state.userId = action.payload;
    },
    setUserName(state, action: { payload: string }) {
      state.username = action.payload;
    },
    setRegistrationToken(state, action: { payload: string }) {
      state.registrationToken = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendPhoneCodeThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendPhoneCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendPhoneCodeThunk.rejected, (state) => {
        state.status = 'error';
      })
     .addCase(verifyOtpThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyOtpThunk.fulfilled, (state, action) => {
        state.status = 'idle';
      })
      .addCase(verifyOtpThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors = extractFieldErrors(action.payload);
        state.error = action.error.message;
      })
      // Credentials
      .addCase(credentialVerification.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(credentialVerification.fulfilled, (state, action) => {
        state.status = 'idle';
        state.maskedEmail = action.payload.maskedEmail;
        state.maskedPhone = action.payload.maskedPhone;
        state.twoFactorToken = action.payload.twoFactorToken;
      })
      .addCase(credentialVerification.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors = extractFieldErrors(action.payload);
        state.error = action.error.message;
      })

      // Select method
      .addCase(select2FAMethodThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(select2FAMethodThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMethod = action.meta.arg.method;
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(select2FAMethodThunk.rejected, (state) => {
        state.status = 'error';
      })

        .addCase(sendEmailCodeThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendEmailCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendEmailCodeThunk.rejected, (state) => {
        state.status = 'error';
      })

      // Verify code
      .addCase(verify2faCodeThunk.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(verify2faCodeThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.status = 'authenticated';
        state.loginStep = 'authenticated';
        state.sessionToken = action.payload.sessionToken;
      })
      .addCase(verify2faCodeThunk.rejected, (state, action) => {
        state.isSubmitting = false;
      })

      // Resend
      .addCase(resend2faCodeThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(resend2faCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(resend2faCodeThunk.rejected, (state) => {
        state.status = 'error';
      })

      // Forgot password
      .addCase(forgotPasswordThunk.pending, (state, action) => {
        state.status = 'loading';
        state.error = undefined;
        state.forgotPasswordEmailAddress = action.meta.arg.emailAddress;
        state.username = action.meta.arg.emailAddress;
        state.fieldErrors = {};
      })
      .addCase(forgotPasswordThunk.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors = extractFieldErrors(action.payload);
        state.error = action.payload?.message ?? action.error.message;
      })

      // Verify recovery email OTP
      .addCase(verifyRecoveryOtpThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyRecoveryOtpThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.recoveryToken = action.payload.recoveryToken;
        state.recoveryMaskedPhone = action.payload.maskedPhone;
      })
      .addCase(verifyRecoveryOtpThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors = extractFieldErrors(action.payload);
        state.error = action.payload?.message ?? action.error.message;
      })

      // Send recovery 2FA
      .addCase(sendRecoveryTwoFactorThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(sendRecoveryTwoFactorThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendRecoveryTwoFactorThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors = extractFieldErrors(action.payload);
        state.error = action.payload?.message ?? action.error.message;
      })

      // Verify recovery 2FA
      .addCase(verifyRecoveryTwoFactorThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyRecoveryTwoFactorThunk.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(verifyRecoveryTwoFactorThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors = extractFieldErrors(action.payload);
        state.error = action.payload?.message ?? action.error.message;
      });
  },
});

export const { clearLoginError, setPassword, setUkek, setDecapsKey, setUserId, setUserName, setRegistrationToken } = authSlice.actions;
export default authSlice.reducer;
